import * as React from 'react'
import { PrintJob,PrintJobStatus } from '@/types'
import { Button } from '@/components/ui/button'
import { Loader2,RefreshCw,XCircle } from 'lucide-react'
import { retryPrintJob,cancelPrintJob } from '@/features/devices/api'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/query-keys'
import { getSelectedShopId } from '@/features/shared'
import { toast } from 'sonner'

export function JobActions({ job }: { job: PrintJob }) {
  const queryClient = useQueryClient()
  const shopId = getSelectedShopId()

  const [isRetrying, setIsRetrying] = React.useState(false)
  const [isCancelling, setIsCancelling] = React.useState(false)

  const canRetry = job.status === PrintJobStatus.FAILED && job.retries < job.maxRetries
  const canCancel = job.status === PrintJobStatus.PENDING || job.status === PrintJobStatus.SENT

  const handleRetry = async () => {
    if (!shopId) return
    setIsRetrying(true)
    try {
      await retryPrintJob(job.id)
      toast.success('Print job retry initiated')
      queryClient.invalidateQueries({ queryKey: queryKeys.printJobs(shopId, {}) })
    } catch {
      toast.error('Failed to retry print job')
    } finally {
      setIsRetrying(false)
    }
  }

  const handleCancel = async () => {
    if (!shopId) return
    setIsCancelling(true)
    try {
      await cancelPrintJob(job.id)
      toast.success('Print job cancelled')
      queryClient.invalidateQueries({ queryKey: queryKeys.printJobs(shopId, {}) })
    } catch {
      toast.error('Failed to cancel print job')
    } finally {
      setIsCancelling(false)
    }
  }

  if (!canRetry && !canCancel) {
    return null
  }

  return (
    <div className="flex flex-col gap-2 pt-4 border-t mt-4">
      {canRetry && (
        <Button
          variant="outline"
          onClick={handleRetry}
          disabled={isRetrying || isCancelling}
          className="w-full"
        >
          {isRetrying ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Retry Job
        </Button>
      )}
      {canCancel && (
        <Button
          variant="destructive"
          onClick={handleCancel}
          disabled={isRetrying || isCancelling}
          className="w-full"
        >
          {isCancelling ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <XCircle className="mr-2 h-4 w-4" />
          )}
          Cancel Job
        </Button>
      )}
    </div>
  )
}
